import { DialogContentText } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import { Keypair } from '@solana/web3.js';
import React, { useMemo, useState, useEffect } from 'react';
import strings from '../localization';
import { useSendTransaction } from '../utils/notifications';
import { createAndInitializeMint } from '../utils/tokens';
import { useUpdateTokenName } from '../utils/tokens/names';
import { abbreviateAddress } from '../utils/utils';
import { useWallet } from '../utils/wallet';
import DialogForm from './DialogForm';
import { getMetadataBySymbol } from '../utils/tokens/metadata';
import { useConnection } from '../utils/connection';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

export default function MintTokenDialog({ open, onClose }) {
  const wallet = useWallet();
  const connection = useConnection();

  const updateTokenName = useUpdateTokenName();
  const [sendTransaction, sending] = useSendTransaction();

  const tokenAccount = useMemo(() => new Keypair(), []);
  const defaultTokenName = `Test Token ${abbreviateAddress(
    tokenAccount.publicKey,
  )}`;
  const defaultTokenSymbol = `TEST${tokenAccount.publicKey
    .toBase58()
    .slice(0, 2)}`;

  const [tokenName, setTokenName] = useState(defaultTokenName);
  const [tokenSymbol, setTokenSymbol] = useState(defaultTokenSymbol);
  const [tokenDecimals, setTokenDecimals] = useState(2);
  const [mintAmount, setMintAmount] = useState(1000);
  const [tokenUri, setTokenUri] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [tokenImageUrl, setTokenImageUrl] = useState(''); 


  // Handle messages from NFT site
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== 'https://nft.domichain.io') return;
      
      if (event.data && event.data.type === 'nft-metadata-uri') {
        setTokenUri(event.data.uri);
        // Store the image URL if available
        if (event.data.imageUrl) {
          setTokenImageUrl(event.data.imageUrl);
        } else {
          // If for some reason there's no image URL, we need to extract it
          fetchMetadataAndExtractImage(event.data.uri);
        }
        setIsUploading(false);
        
        window.focus();
      }
    };
  
    if (!window.cordova) {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, []);

  const fetchMetadataAndExtractImage = async (metadataUri) => {
    try {
      const response = await fetch(metadataUri);
      const metadata = await response.json();
      
      if (metadata.images && metadata.images.length > 0 && metadata.images[0].url) {
        setTokenImageUrl(metadata.images[0].url);
      }
    } catch (error) {
      console.error('Failed to extract image URL from metadata:', error);
    }
  };

  const handleUploadLogo = () => {
    setIsUploading(true);
    
    // Open NFT creator in new window with parameters
    const nftCreatorUrl = new URL('https://nft.domichain.io/create-nft');
    nftCreatorUrl.searchParams.append('fromWallet', 'true');
    nftCreatorUrl.searchParams.append('name', tokenName);
    nftCreatorUrl.searchParams.append('symbol', tokenSymbol);

    const url = nftCreatorUrl.toString();
    
    // Check if we're in an android environment
    if (window.cordova && window.cordova.platformId === 'android') {
      // Use InAppBrowser plugin for Android
      const settings = {
        location: 'yes',
        fullscreen: 'no',
        hidden: 'no',
        clearcache: 'no',
        clearsessioncache: 'no',
        zoom: 'no',
        toolbarcolor: '#2196f3',
        navigationbuttoncolor: '#ffffff',
        closebuttoncolor: '#ffffff',
        hardwareback: 'yes',
        hideurlbar: 'no'
      };
      
      const settingsString = Object.entries(settings)
        .map(([key, value]) => `${key}=${value}`)
        .join(',');
      
      if (window.navigateToPage) {
        window.navigateToPage(url);
        return;
      }

      const browserRef = window.cordova.InAppBrowser.open(
        url,
        '_blank',
        settingsString
      );
      
      // Event listener for messages from the NFT site
      browserRef.addEventListener('loadstop', (event) => {
        const currentBrowserUrl = event.url;
        
        // Inject the same code that Browser.js injects
        browserRef.executeScript({
          code: `
            window.dwallet = {
              postMessage: function dwalletPostMessage(message) {
                message.metadata = { dappTitle: document.title };
                webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(message));
              }
            };
            
            // Signal to the page that we're in the wallet browser
            window.isInDWalletBrowser = true;
            
            // Dispatch a custom event that the page can listen for
            document.dispatchEvent(new CustomEvent('dwallet-ready', { 
              detail: { publicKey: "${wallet.publicKey.toBase58()}" }
            }));
          `
        });
      });
      
      // Handle messages from the NFT site
      browserRef.addEventListener('message', (event) => {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          if (data && data.type === 'nft-metadata-uri') {
            setTokenUri(data.uri);
            
            if (data.imageUrl) {
              setTokenImageUrl(data.imageUrl);
            } else {
              fetchMetadataAndExtractImage(data.uri);
            }
            
            setIsUploading(false);
            browserRef.close();
          } else if (data && data.method === 'connect') {
            const connectResponse = {
              id: data.id,
              method: 'connected',
              params: {
                publicKey: wallet.publicKey.toBase58(),
                autoApprove: true
              }
            };
            
            browserRef.executeScript({
              code: `
                window.postMessage(${JSON.stringify(connectResponse)});
                console.debug('Message from DWallet:', ${JSON.stringify(connectResponse)});
              `
            });
          }
        } catch (error) {
          console.error('Error processing message from InAppBrowser:', error);
        }
      });
      
      browserRef.addEventListener('exit', () => {
        if (isUploading) {
          setIsUploading(false);
        }
      });
    } else {
      window.open(url, 'nft_creator', 'width=800,height=700');
    }
  };

  const mintNewToken = async () => {
    const decimals = parseFloat(tokenDecimals);
    if (!decimals || decimals <= 0) {
      throw new Error('Invalid decimal places');
    }

    const amount = Math.round(parseFloat(mintAmount) * 10 ** decimals);
    if (!amount || amount <= 0) {
      throw new Error('Invalid amount');
    }

    const existingTokens = await getMetadataBySymbol(connection, tokenSymbol);
    if (existingTokens.length > 0) {
      throw new Error(
        `Token symbol "${tokenSymbol}" already exists, choose a different one`,
      );
    }

    const uriToUse = tokenImageUrl || tokenUri;

    return await createAndInitializeMint({
      connection,
      owner: wallet,
      mint: tokenAccount,
      name: tokenName,
      symbol: tokenSymbol,
      amount: amount,
      decimals: decimals,
      initialAccount: new Keypair(),
      uri: uriToUse,
    });
  };

  const handleOnPressMint = () => {
    sendTransaction(mintNewToken(), {
      onSuccess: () => {
        updateTokenName(
          tokenAccount.publicKey,
          tokenName || defaultTokenName,
          tokenSymbol || defaultTokenSymbol,
          true,
        );
        onClose();
      },
      onError: (error) => {
        console.log('Failed to mint a token:', error);
        onClose();
      },
    });
  };

  return (
    <DialogForm open={open} onClose={onClose} onSubmit={onClose} fullWidth>
      <DialogTitle>{strings.mintNewToken}</DialogTitle>
      <DialogContent>
        <DialogContentText>{strings.mintNewTokenDescription}</DialogContentText>
        <TextField
          label={strings.tokenName}
          fullWidth
          variant="outlined"
          margin="normal"
          value={tokenName}
          onChange={(e) => setTokenName(e.target.value)}
        />
        <TextField
          label={strings.tokenSymbol}
          fullWidth
          variant="outlined"
          margin="normal"
          value={tokenSymbol}
          onChange={(e) => setTokenSymbol(e.target.value)}
        />
        <TextField
          label={strings.tokenDecimals}
          fullWidth
          variant="outlined"
          margin="normal"
          type="number"
          value={tokenDecimals}
          onChange={(e) => setTokenDecimals(e.target.value)}
        />
        <TextField
          label={strings.amount}
          fullWidth
          variant="outlined"
          margin="normal"
          type="number"
          value={mintAmount}
          onChange={(e) => setMintAmount(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<CloudUploadIcon />}
          onClick={handleUploadLogo}
          disabled={isUploading}
          style={{ margin: '16px 0' }}
        >
          {isUploading ? 'Waiting for logo upload...' : 'Upload Logo'}
        </Button>
        
        {tokenUri && (
          <TextField
            label="Metadata URI"
            fullWidth
            variant="outlined"
            margin="normal"
            value={tokenUri}
            InputProps={{
              readOnly: true,
            }}
            helperText="Metadata URI received from NFT creator"
          />
        )}
        
        {/* Show the user the logo and uri */}

        {tokenImageUrl && (
        <div style={{ margin: '16px 0', textAlign: 'center' }}>
          <img 
            src={tokenImageUrl} 
            alt="Token Logo Preview" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '200px', 
              border: '1px solid #ccc', 
              borderRadius: '4px' 
            }} 
          />
          <Typography variant="caption" display="block">
            Token Logo Preview
          </Typography>
        </div>
      )}
      {tokenUri && (
        <TextField
          label="Metadata URI"
          fullWidth
          variant="outlined"
          margin="normal"
          value={tokenUri}
          InputProps={{
            readOnly: true,
          }}
          helperText="Metadata URI received from NFT creator"
        />
      )}
      {tokenImageUrl && (
        <TextField
          label="Image URL"
          fullWidth
          variant="outlined"
          margin="normal"
          value={tokenImageUrl}
          InputProps={{
            readOnly: true,
          }}
          helperText="This URL will be used as the token URI"
        />
      )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{strings.close}</Button>
        <Button
          type="submit"
          color="primary"
          onClick={handleOnPressMint}
          disabled={sending}
        >
          Mint
        </Button>
      </DialogActions>
    </DialogForm>
  );
}
